import { ReactNode } from 'react'

import { cn } from 'src/utils'

export interface DetailFieldProps {
  label: string
  value?: ReactNode
  /**
   * What to show when there is no value. On the admin directory screens the
   * absence of a value is the finding, so it is stated rather than left blank.
   */
  emptyLabel?: string
  /** Renders the value in a monospace face — for ids, emails and DOT numbers. */
  mono?: boolean
  /**
   * Columns the field occupies in its {@link DetailSection} grid. Give a long
   * value like an address two, so it sits on one line instead of wrapping into
   * a column sized for a phone number.
   */
  span?: 1 | 2
  className?: string
}

// Whole strings, for the same reason as the column classes in DetailSection.
const SPAN_CLASS: Record<1 | 2, string> = {
  1: '',
  2: 'sm:col-span-2',
}

const isEmpty = (value: ReactNode) =>
  value === null ||
  value === undefined ||
  value === false ||
  (typeof value === 'string' && value.trim() === '')

/** One label/value pair inside a {@link DetailSection}. */
const DetailField = ({
  label,
  value,
  emptyLabel = 'Not on file',
  mono = false,
  span = 1,
  className,
}: DetailFieldProps) => (
  <div className={cn('flex flex-col gap-1', SPAN_CLASS[span], className)}>
    <div className="text-xs font-medium uppercase tracking-wide text-doubleNickel-gray-500">
      {label}
    </div>
    {isEmpty(value) ? (
      <div className="text-sm italic text-doubleNickel-gray-400">
        {emptyLabel}
      </div>
    ) : (
      <div
        className={cn(
          'break-words text-sm text-doubleNickel-gray-900',
          mono && 'font-mono'
        )}
      >
        {value}
      </div>
    )}
  </div>
)

export default DetailField
